/* eslint-disable @typescript-eslint/naming-convention */
import { useRef, useCallback, useState, useContext } from "react";
import {
    CouponMessageCard,
    ICouponCard,
    ICouponCards,
    INextBestCoupon,
    useMountedRef,
    captureError,
} from "@swiggy-private/core-ui";

import {
    CheckoutContext,
    CheckoutState,
    getBridgeSdk,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    DineoutCartContext,
    getDineoutFlowType,
    DineoutCartActionTypes,
    IDineoutLocalCart as ILocalCart,
} from "@swiggy-private/checkout-core";
import {
    APPLIED_TEXT,
    CASHBACK_TEXT,
    COUPON_SAVING_TEXT,
    COUPON_TOAST_CTA,
    COUPON_TOAST_IMAGE,
    COUPON_TOAST_SUCCESS_CTA,
    COUPON_TOAST_SUCCESS_IMAGE,
    FAILED_TEXT,
    OOPS_SOMETHING_WENT_WRONG,
} from "@dineout-cart-client/constants";
import { IDineoutCartResponse, ICouponModalCard, IFetchCouponsParams } from "@dineout-cart-client/interfaces";
import { useBillPaymentAnalytics } from "./useDineoutCartAnalytics";
import { format } from "@swiggy-private/js-utils";
import { isEventReservationCart } from "@dineout-cart-client/helpers/cartHelper";
import { getCartPageCheckoutState } from "@dineout-cart-client/helpers/CouponListing";
import { prepareLocalCartFromReviewedCart } from "@dineout-cart-client/utils/index";
import { applyCouponWrapper, fetchCouponsWrapper } from "@dineout-cart-client/apis/Coupon";
import { Err, Result } from "@swiggy-private/js-fp";
import { ApiResponse } from "@swiggy-private/core-utils";

interface ICouponsPayload {
    data: ICouponCard[] | null;
    error?: {
        httpStatusCode?: number;
        errorPayload?: unknown;
    };
}

interface ICouponModalDataParams {
    coupon: string;
    amount: number;
    valid: boolean;
    message?: string;
    couponCard?: ICouponModalCard;
}


export const useApplyCoupon = (): {
    syncApplyCouponCart: (localCart: ILocalCart) => void;
    updateCouponState: boolean;
    isFetching: boolean;
    couponsPayload: ICouponsPayload;
    isApplyingCoupon: boolean;
    isRemovingCoupon?: boolean;
    couponModalData?: CouponMessageCard;
    showPopup?: boolean;
    onCloseModal: VoidFunction;
    errorMessage: string;
    fetchCoupons: (params: IFetchCouponsParams) => void;
    clearErrorMessage: VoidFunction;
    nextBestCoupon: INextBestCoupon | null;
    resetCouponListingPage: VoidFunction;
    handleCouponListingPageCouponRemove: () => void;
    hasInitiallyFetched: boolean;
} => {
    const { cartState, setCartState, cartType } = useContext(DineoutCartContext);
    const { localCart } = cartState;
    const { onImpressionCoupon, onClickCouponEvent } = useBillPaymentAnalytics();
    const { setCheckoutState } = useContext(CheckoutContext);
    const [updateCouponState, setUpdateCouponState] = useState<boolean>(false);
    const [couponsPayload, setCouponsPayload] = useState<ICouponsPayload>({ data: null });
    const [nextBestCoupon, setNextBestCoupon] = useState<INextBestCoupon | null>(null);
    const [isFetching, setFetching] = useState<boolean>(false);
    const hasInitiallyFetched = useRef<boolean>(false);
    const [isApplyingCoupon, setApplyingCoupon] = useState<boolean>(false);
    const [isRemovingCoupon, setRemovingCoupon] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [showPopup, setShowPopup] = useState<boolean>(false);
    const [couponModalData, setCouponModalData] = useState({} as CouponMessageCard);

    const apiCallRef = useRef(false);
    const isMountedRef = useMountedRef();

    const clearErrorMessage = useCallback(() => {
        setErrorMessage("");
    }, []);

    const resetCouponListingPage = useCallback(() => {
        setApplyingCoupon(false);
        clearErrorMessage();
        setCouponsPayload({ data: null });
        setFetching(false);
        setNextBestCoupon(null);
        setShowPopup(false);
        hasInitiallyFetched.current = false;
    }, [clearErrorMessage]);

    const onCloseModal = useCallback(() => {
        onClickCouponEvent({ status: Boolean(!localCart?.cartMetaData?.couponCode) });
        setShowPopup(false);
        resetCouponListingPage();
    }, [localCart?.cartMetaData?.couponCode, onClickCouponEvent, resetCouponListingPage]);

    const updateModalData = useCallback(
        ({ coupon, amount, valid, message, couponCard }: ICouponModalDataParams) => {
            setShowPopup(true);
            if (valid) {
                onImpressionCoupon({ status: true });

                setCouponModalData({
                    icon: couponCard?.icon || COUPON_TOAST_SUCCESS_IMAGE,
                    title: couponCard?.title || format(APPLIED_TEXT, coupon),
                    description:
                        couponCard?.description || (amount > 0 ? format(COUPON_SAVING_TEXT, amount) : CASHBACK_TEXT),
                    cta: couponCard?.cta || COUPON_TOAST_SUCCESS_CTA,
                });
                return;
            }
            onImpressionCoupon({ status: false });
            setCouponModalData({
                icon: COUPON_TOAST_IMAGE,
                title: FAILED_TEXT,
                description: message || OOPS_SOMETHING_WENT_WRONG,
                cta: COUPON_TOAST_CTA,
            });
        },
        [onImpressionCoupon],
    );

    const apiFailure = useCallback(
        (err) => {
            const parsedError = Err(err);
            apiCallRef.current = false;
            const errorMessage = parsedError.err().unwrap().error.statusMessage;
            if (isMountedRef.current) {
                captureError(err);
                setUpdateCouponState(false);
                setApplyingCoupon(false);
                updateModalData({ coupon: "", amount: 0, valid: false, message: errorMessage });
                const cartPageCheckoutState = getCartPageCheckoutState(cartType);
                setCheckoutState(cartPageCheckoutState as CheckoutState);
            }
        },
        [cartType, isMountedRef, setCheckoutState, updateModalData],
    );

    const syncApplyCouponCart = useCallback(
        (localCart: ILocalCart) => {
            setApplyingCoupon(true);
            setUpdateCouponState(true);
            applyCouponWrapper(localCart)
                .then((response: Result<IDineoutCartResponse, Error | ApiResponse>) => {
                    if (response.isErr()) {
                        throw response;
                    }
                    const cartResponse = response.unwrap() ?? null;

                    setCartState({
                        payload: { localCart: localCart, reviewedCart: cartResponse },
                        type: DineoutCartActionTypes.UPDATE_LOCAL_AND_REVIEWED_CART,
                    });

                    //TODO this check to be removed when natie side changes were done
                    if (isEventReservationCart(cartType)) {
                        /* Flow type is required for the dineout cart bridge functions, so that app can identify which cart flow is being used e.g. table reservation, event booking or bill payment */
                        /* If we don't pass the flow type, the bridge functions will not be able to identify which cart flow to update/delete 
                        /* and this might create issues when the user is using multiple carts in the same app session */
                        const flowType = getDineoutFlowType(cartType);
                        const updatedLocalCart = prepareLocalCartFromReviewedCart(
                            flowType,
                            localCart,
                            cartResponse?.data,
                        );
                        getBridgeSdk()
                            .dineoutUpdateLocalCart(updatedLocalCart)
                            .catch(() => void 0);
                    }
                    updateModalData({
                        coupon: localCart?.cartMetaData?.couponCode || "",
                        amount: Number(cartResponse?.data?.coupon?.amount) || 0,
                        valid: true,
                        couponCard: cartResponse?.data?.couponCard,
                    });

                    return cartResponse;
                })
                .then(() => {
                    if (isMountedRef.current) {
                        setApplyingCoupon(false);
                        setUpdateCouponState(false);
                        setApplyingCoupon(false);
                        const cartPageCheckoutState = getCartPageCheckoutState(cartType);
                        setCheckoutState(cartPageCheckoutState as CheckoutState);
                    } else {
                        setApplyingCoupon(false);
                        setUpdateCouponState(false);
                    }
                })
                .catch(apiFailure);
        },
        [apiFailure, setCartState, cartType, updateModalData, isMountedRef, setCheckoutState],
    );

    const fetchCoupons = useCallback(
        async (params: IFetchCouponsParams) => {
            setFetching(true);
            hasInitiallyFetched.current = true;
            const lat = GlobalHeaderState.getHeader(SwgyHeaderKeysList.lat);
            const lng = GlobalHeaderState.getHeader(SwgyHeaderKeysList.lng);
            try {
                const response = await fetchCouponsWrapper({
                    lat,
                    lng,
                    resId: params.restaurantId,
                    cartValue: params.cartValue,
                    dealCategory: params.dealCategory || "",
                    entityId: params.entityId || "",
                    isPartPaySelected: params.isPartPaySelected ?? false,
                    tableId: params.tableId || "",
                });
                if (response.isErr()) {
                    throw response;
                }

                const couponsResponse = response.unwrap();
                const couponGroupArr = couponsResponse?.response?.success?.cards?.map(
                    (each: ICouponCards) => each?.card?.card,
                );
                couponGroupArr && setCouponsPayload({ data: couponGroupArr });
                setFetching(false);
            } catch (error) {
                setFetching(false);
                const errorObj = {
                    errorPayload: error,
                };
                setCouponsPayload({ data: null, error: errorObj });
            }
        },
        [],
    );

    const handleCouponListingPageCouponRemove = useCallback(async () => {
        if (updateCouponState || apiCallRef.current) {
            return;
        }

        apiCallRef.current = true;
        setRemovingCoupon(true);
        setUpdateCouponState(true);
        setFetching(true);

        try {
            setUpdateCouponState(false);
            setFetching(false);
            setRemovingCoupon(false);
        } catch (err) {
            setFetching(false);
            setRemovingCoupon(false);
            apiFailure(err);
        }
    }, [apiFailure, updateCouponState]);

    return {
        fetchCoupons,
        syncApplyCouponCart,
        updateCouponState,
        couponsPayload,
        isFetching,
        isApplyingCoupon,
        couponModalData,
        onCloseModal,
        showPopup,
        isRemovingCoupon,
        errorMessage,
        clearErrorMessage,
        nextBestCoupon,
        resetCouponListingPage,
        handleCouponListingPageCouponRemove,
        hasInitiallyFetched: hasInitiallyFetched.current,
    };
};
